// =============================================================================
// REVIEWER COMPARISON LOG — Jason's exact honesty-reviewer ruling, made
// auditable: "Replace, BUT keep both in the back garden for now — we need to
// see how far to trim the weeds and see the Delta."
//
// BullshitDetectorService.detectBullshit is now the PRIMARY/surfaced
// reviewer on cover letters + outreach (what the FE renders).
// AdversarialReviewerService.adversarialReview keeps running, demoted to a
// SHADOW pass — best-effort, never blocks generation, never surfaced to the
// user. This table is where the two outputs + their delta land, for Jason's
// own evaluation only (never a user-facing surface).
//
// ONE ROW per shadow-comparison run. Written by
// apps/api/services/ReviewerComparisonService.ts, read only by Jason
// (directly via Neon, or a future admin-only surface — none built yet).
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool.
// =============================================================================

import { sql } from 'drizzle-orm';
import { boolean, index, integer, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const reviewerComparisonLog = pgTable(
  'reviewer_comparison_log',
  {
    id: id(),
    // Nullable: a pre-auth or system-triggered comparison still logs (mirrors
    // errorLogs.userId's own nullable-for-honesty posture).
    userId: text('user_id'),
    // 'cover_letter' | 'outreach_email' — matches BullshitDetectorArtifactKind's
    // live union; free varchar here (not a DB enum) so a future artifact kind
    // never needs a migration to log against, only a code-side union update.
    artifactKind: varchar('artifact_kind', { length: 32 }).notNull(),
    jobId: text('job_id'),
    // The PRIMARY/surfaced reviewer's full result (BullshitDetectorResult).
    bullshitResult: jsonb('bullshit_result').notNull(),
    // The SHADOW reviewer's full result (AdversarialReviewResult), nullable —
    // the shadow pass is best-effort and its own failure must never block
    // logging the primary result alone.
    adversarialResult: jsonb('adversarial_result'),
    // Coarse verdict agreement, computed at write time so Jason's own review
    // doesn't require re-deriving it from the two blobs every time:
    // did both reviewers land on the same "keep reading" call?
    verdictsAgree: boolean('verdicts_agree'),
    // Cheap headline numbers pulled out of the two blobs for fast scanning
    // without parsing jsonb — never authoritative, the full blobs above are.
    bullshitFindingCount: integer('bullshit_finding_count'),
    adversarialFlagCount: integer('adversarial_flag_count'),
    ...timestamps,
  },
  (t) => ({
    userCreatedIdx: index('reviewer_comparison_log_user_created_idx').on(t.userId, t.createdAt),
    artifactKindIdx: index('reviewer_comparison_log_artifact_kind_idx').on(t.artifactKind),
  }),
);
